import { useCallback, useEffect, useRef, useState } from 'react';
import { ProviderMark, type ProviderId } from '@/components/ui/ProviderMark';

import {
  ProviderCancelled, ProviderUnavailable,
  credentialFor, inTelegram, isAvailable, DEV_CREDENTIALS,
  type Provider,
} from '@/api/providers';
import { Icon } from '@/components/ui/Icon';
import { useSession } from '@/store/session';

/**
 * The sign-in gate — Guest, Google, Apple, Telegram. No email, no password.
 *
 * This screen exists because the app used to create a guest account silently at
 * boot, which took the choice away from the player and made the other three
 * providers unreachable.
 *
 * Guest is presented first and without friction, because that is the fastest
 * route into the cave — but it carries the honest warning that progress lives on
 * this device only, since a guest who reinstalls loses everything.
 */

interface Option {
  id: Provider | 'guest';
  label: string;
  hint: string;
}


/* Typed to the three providers, not `Option`, which also admits 'guest'. Guest is its own
   button below and never reaches this list — but while the array said otherwise, `ProviderMark`
   had to accept an id it can draw no mark for, and its fall-through would have put the Google
   logo on a guest row. Narrowing here is what makes that unrepresentable. */
const PROVIDERS: { id: ProviderId; label: string; hint: string }[] = [
  { id: 'google',   label: 'Continue with Google',   hint: 'Keeps your progress on any device' },
  { id: 'apple',    label: 'Continue with Apple',    hint: 'Keeps your progress on any device' },
  { id: 'telegram', label: 'Continue with Telegram', hint: 'Play from your Telegram chats' },
];

export function Login() {
  const playAsGuest = useSession((s) => s.playAsGuest);
  const signInWith = useSession((s) => s.signInWith);
  const storeError = useSession((s) => s.error);

  const [busy, setBusy] = useState<Option['id'] | null>(null);
  const [error, setError] = useState<string | null>(null);
  /* Google Identity Services draws its own button and refuses to run from a
     synthetic click in most webviews, so it needs a real mounted element. */
  const googleMount = useRef<HTMLDivElement | null>(null);

  useEffect(() => { setError(storeError); }, [storeError]);

  /* Inside Telegram there is already a signed credential on the window, so sign
     the player straight in rather than asking them to tap a Telegram button
     inside Telegram. */
  const autoTelegram = useRef(false);
  useEffect(() => {
    if (autoTelegram.current || DEV_CREDENTIALS || !inTelegram()) return;
    autoTelegram.current = true;
    void (async () => {
      try {
        setBusy('telegram');
        await signInWith('telegram', await credentialFor('telegram'));
      } catch {
        setBusy(null);   // fall back to the buttons
      }
    })();
  }, [signInWith]);

  const choose = useCallback(async (option: Option['id']) => {
    setError(null);
    setBusy(option);
    try {
      if (option === 'guest') {
        await playAsGuest();
        return;
      }
      const credential = await credentialFor(option, googleMount.current ?? undefined);
      await signInWith(option, credential);
    } catch (e) {
      if (e instanceof ProviderCancelled) setError(null);
      else if (e instanceof ProviderUnavailable) setError(e.message);
      else setError('Sign-in failed. Try again.');
    } finally {
      setBusy(null);
    }
  }, [playAsGuest, signInWith]);

  return (
    <div className="relative mx-auto flex h-full w-full max-w-[520px] flex-col overflow-y-auto
                    bg-[radial-gradient(120%_70%_at_50%_-10%,#2A1B10_0%,transparent_60%)]
                    px-6 py-8 sm:my-auto sm:h-[min(100%,940px)] sm:rounded-[26px]">
      {/* ── brand ── */}
      <header className="shrink-0 pt-6 text-center">
        <div className="mx-auto grid h-20 w-20 place-items-center rounded-2xl
                        border border-[rgba(255,196,140,.25)]
                        bg-[linear-gradient(#3A2718,var(--cave-floor))]
                        shadow-[0_0_28px_rgba(255,169,69,.18)]">
          <Icon name="pick" size={38} className="text-torch-warm" />
        </div>
        <h1 className="mt-4 font-pixel text-lg tracking-wide text-torch-core">SHWETAP</h1>
        <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.18em] text-ink-dim">
          Tap · Dig · Discover
        </p>
        <p className="mt-4 text-xs leading-relaxed text-ink-dim">
          An endless cave, a torch, and whatever your axe can break.
        </p>
      </header>

      {/* ── the four ways in ── */}
      <div className="mt-8 flex flex-1 flex-col justify-center gap-2.5">
        <button
          type="button"
          onClick={() => void choose('guest')}
          disabled={busy !== null}
          className="flex w-full items-center gap-3 rounded-sm border
                     border-[rgba(255,220,170,.5)] bg-[linear-gradient(#FFA945,#E0761C)]
                     px-4 py-3.5 text-left text-[#2A1503]
                     shadow-[0_4px_0_-1px_#8C440C] transition-transform
                     active:translate-y-0.5 disabled:opacity-50"
        >
          <Icon name="hero" size={20} />
          <span className="min-w-0 flex-1">
            <span className="block font-pixel text-[9px] tracking-wide">
              {busy === 'guest' ? 'ENTERING THE CAVE…' : 'PLAY AS GUEST'}
            </span>
            <span className="mt-0.5 block text-[10px] opacity-75">
              Start now — progress stays on this device
            </span>
          </span>
        </button>

        <div className="my-1 flex items-center gap-3" aria-hidden>
          <span className="h-px flex-1 bg-[var(--hairline)]" />
          <span className="font-pixel text-[7px] tracking-widest text-ink-dim">OR KEEP IT SAFE</span>
          <span className="h-px flex-1 bg-[var(--hairline)]" />
        </div>

        {PROVIDERS.map((p) => {
          const available = isAvailable(p.id as Provider);
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => void choose(p.id)}
              disabled={busy !== null || !available}
              className="flex w-full items-center gap-3 rounded-sm border border-ui-lit
                         bg-[linear-gradient(#3A2718,var(--cave-floor))] px-4 py-3
                         text-left shadow-[0_3px_0_-1px_rgba(0,0,0,.55)]
                         transition-transform active:translate-y-0.5
                         disabled:opacity-40 disabled:grayscale"
            >
              <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full
                               bg-black/40 text-ink" aria-hidden>
                <ProviderMark id={p.id} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-bold text-ink">
                  {busy === p.id ? 'Signing in…' : p.label}
                </span>
                <span className="mt-0.5 block text-[10px] text-ink-dim">
                  {available ? p.hint
                    : p.id === 'telegram' ? 'Open ShweTap inside Telegram'
                      : 'Not available on this device'}
                </span>
              </span>
            </button>
          );
        })}

        {/* Google draws its own button here when the real flow runs. */}
        <div ref={googleMount} className="flex justify-center empty:hidden" />

        {error && (
          <p role="alert" className="mt-1 rounded-md border border-[rgba(224,85,63,.45)]
                                     bg-black/60 px-3 py-2 text-center text-[11px] text-[#FF9A8B]">
            {error}
          </p>
        )}
      </div>

      <footer className="shrink-0 pt-6 text-center">
        <p className="text-[10px] leading-relaxed text-ink-dim">
          You can link Google, Apple or Telegram later from Settings —
          your guest progress comes with you.
        </p>
        <p className="mt-3 font-pixel text-[7px] tracking-wider text-ink-dim opacity-60">
          ReiwaSakura.Tech
        </p>
      </footer>
    </div>
  );
}
